import React, { type ReactNode } from "react";

import { formatDuration } from "../../utils";
import { ReadingPair } from "./processSidebarUi";

// A row in the app-usage list: an app (or the bundled "Other apps") with the seconds it
// accounts for and its share of the scope's total time. `pct` drives the label while `seconds`
// drives the bar width and the duration caption. `onClick` makes the row interactive (e.g. the
// Overview tab filters its process breakdown by the clicked app); omit it for static lists.
export type AppUsageRow = {
  name: string;
  seconds: number;
  pct: number;
  color: string;
  iconUri: string | null;
  /** Drawn in place of `iconUri`, for rows that are not apps (e.g. step types). */
  icon?: ReactNode;
  isOther?: boolean;
  onClick?: () => void;
  /** Pointing at (or focusing) the row calls it with true, leaving with false. */
  onHover?: (hovering: boolean) => void;
};

// Ranked list of app-usage bars: brand logo (or a color chip when we have no icon) + a
// relative-duration bar in one common hue + a "mm:ss" and "%" caption. The most-used app fills the
// track and the remaining bars are scaled against it, matching the time-sink lists. The logos
// carry app identity; a shared bar color avoids implying that their colors encode another
// dimension. Reused across the process/instance overview, the per-node panel, and the Overview
// tab's Time Allocation. `barColor` overrides that shared hue where the list itself belongs to
// something colour-coded — compare mode gives each side its own A/B colour. `instanceCount`
// switches the caption from total time to average time per instance, so the list reads in the
// same unit as an overview's headline average duration.
const AppUsageBars = ({
  rows,
  barColor,
  instanceCount,
  scale = "relative",
  maxSeconds: sharedMaxSeconds,
}: {
  rows: AppUsageRow[];
  barColor?: string;
  instanceCount?: number;
  /** Share uses a 0–100% track instead of scaling to the leading app. */
  scale?: "relative" | "share";
  /** A common time baseline when multiple lists are compared. */
  maxSeconds?: number;
}) => {
  const maxSeconds = sharedMaxSeconds ?? Math.max(0, ...rows.map(row => row.seconds));

  return (
    <ul className="space-y-1.5">
      {rows.map(row => {
        const barPct =
          scale === "share"
            ? row.pct
            : maxSeconds > 0
              ? Math.max(2, (row.seconds / maxSeconds) * 100)
              : 0;
        const content = (
          <>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              {row.icon ? (
                row.icon
              ) : row.iconUri ? (
                <img src={row.iconUri} alt="" className="h-5 w-5 object-contain" />
              ) : (
                <span className="h-3 w-3 rounded-sm" style={{ background: row.color }} />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={`truncate text-sm ${row.isOther ? "pr-0.5 italic text-text-mid" : "text-text"}`}
                >
                  {row.name}
                </span>
                <ReadingPair
                  value={formatDuration(
                    instanceCount && instanceCount > 0 ? row.seconds / instanceCount : row.seconds,
                  )}
                  share={`${Math.round(row.pct)}%`}
                  className="text-xs"
                />
              </div>
              <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-app-usage-track">
                <div
                  className={`h-full rounded-full transition-colors ${
                    row.isOther
                      ? "bg-app-usage-other"
                      : barColor
                        ? ""
                        : `bg-app-usage/70 ${row.onClick ? "group-hover:bg-app-usage/90" : ""}`
                  }`}
                  style={{
                    width: `${Math.min(100, barPct)}%`,
                    // An override paints only the real apps: "Other apps" stays the neutral grey
                    // so the residual is never mistaken for one more of the list's own bars.
                    ...(barColor && !row.isOther ? { background: barColor } : {}),
                  }}
                />
              </div>
            </div>
          </>
        );
        // Category icons sit in a tinted tile, which needs more room than a bare logo.
        const gap = row.icon ? "gap-3" : "gap-2";
        return (
          <li
            key={row.name}
            onPointerEnter={row.onHover && (() => row.onHover?.(true))}
            onPointerLeave={row.onHover && (() => row.onHover?.(false))}
            onFocus={row.onHover && (() => row.onHover?.(true))}
            onBlur={row.onHover && (() => row.onHover?.(false))}
          >
            {row.onClick ? (
              <button
                type="button"
                onClick={row.onClick}
                className={`group flex w-full items-center ${gap} rounded-md text-left hover:bg-surface-strong/50`}
              >
                {content}
              </button>
            ) : (
              <div className={`flex items-center ${gap}`}>{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default AppUsageBars;
